// The Canvas component (spec 16 §3-4).
//
// Reads a (validated) CanvasPlan, buckets it by widget kind, renders
// in canonical order. Owns the two-column layout: when the container
// is wide enough, entity cards move to a right rail.
//
// Used by both surfaces:
//   - The cmd+K palette overlay (always single-column)
//   - The /projects/:id/ask route (single or two-column based on width)

import { useState } from "react";

import {
	AnswerProse,
	ClosestMatches,
	Diagnostic,
	EntityCard,
	Followups,
	KeyFacts,
	SourcesStrip,
	Stagger,
} from "@/components/canvas/widgets";
import { bucketize, type CanvasPlan } from "@/components/canvas/types";

interface CanvasProps {
	plan: CanvasPlan;
	/** Force single-column even on wide viewports (used by the
	 *  palette overlay which is itself constrained in width). */
	singleColumn?: boolean;
	/** Click handler for follow-up pills. */
	onFollowup?: (query: string) => void;
	/** Click handler for source / entity cards. Receives the node id
	 *  the card points at; the palette wires this to close + navigate
	 *  to the graph view focused on that node. */
	onFocusNode?: (nodeId: string) => void;
}

export function Canvas({
	plan,
	singleColumn,
	onFollowup,
	onFocusNode,
}: CanvasProps) {
	const buckets = bucketize(plan);
	const [hoveredRef, setHoveredRef] = useState<number | null>(null);

	const knownRefs = new Set(
		(buckets.sourcesStrip?.sources ?? []).map((s) => s.ref),
	);

	const focusSource = (ref: number) => {
		setHoveredRef(ref);
		const el = document.getElementById(`source-${ref}`);
		if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
		// Drop the highlight after a moment.
		setTimeout(() => setHoveredRef(null), 1400);
	};

	const mainColumn = (
		<div className="space-y-5">
			{buckets.sourcesStrip && (
				<Stagger index={0}>
					<SourcesStrip
						sources={buckets.sourcesStrip.sources}
						highlightedRef={hoveredRef}
						onHover={setHoveredRef}
						onFocusNode={onFocusNode}
					/>
				</Stagger>
			)}
			{buckets.prose && (
				<Stagger index={1}>
					<AnswerProse
						text={buckets.prose.text}
						citation_refs={buckets.prose.citation_refs}
						knownRefs={knownRefs}
						onCiteClick={focusSource}
					/>
				</Stagger>
			)}
			{buckets.keyFacts.map((kf, i) => (
				<Stagger key={`kf-${i}`} index={2 + i}>
					<KeyFacts {...kf} />
				</Stagger>
			))}
			{/* Entity cards inline when single-column. */}
			{singleColumn && buckets.entityCards.length > 0 && (
				<div className="space-y-2">
					<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
						Featured
					</div>
					<div className="space-y-2">
						{buckets.entityCards.map((ec, i) => (
							<Stagger key={`ec-${i}`} index={3 + i}>
								<EntityCard {...ec} onFocusNode={onFocusNode} />
							</Stagger>
						))}
					</div>
				</div>
			)}
			{buckets.followups.map((f, i) => (
				<Stagger key={`fu-${i}`} index={4 + i}>
					<Followups {...f} onFollowup={onFollowup} />
				</Stagger>
			))}
			{buckets.closestMatches.map((cm, i) => (
				<Stagger key={`cm-${i}`} index={5 + i}>
					<ClosestMatches {...cm} />
				</Stagger>
			))}
			{buckets.diagnostic && (
				<Stagger index={6}>
					<Diagnostic {...buckets.diagnostic} />
				</Stagger>
			)}
		</div>
	);

	if (singleColumn || buckets.entityCards.length === 0) {
		return mainColumn;
	}

	return (
		<div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
			<div>{mainColumn}</div>
			<aside className="space-y-3 lg:sticky lg:top-2 lg:self-start">
				<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
					Entities
				</div>
				<div className="space-y-2">
					{buckets.entityCards.map((ec, i) => (
						<Stagger key={`ec-rail-${i}`} index={2 + i}>
							<EntityCard {...ec} onFocusNode={onFocusNode} />
						</Stagger>
					))}
				</div>
			</aside>
		</div>
	);
}
